import {
  SUMMARY_ENTRY_START,
  SUMMARY_ENTRY_ACCEPTED,
  SUMMARY_ENTRY_SUCCESS,
  SUMMARY_ENTRY_ERROR,
  SUMMARY_ENTRY_CANCELLED,
  SUMMARY_ENTRY_FEEDBACK,
  SUMMARY_ENTRY_ADD_VALID_RESULT_NAME,
  RESET_SUMMARY_STATE,
  MAX_SUMMARIES_PER_CHAIN,
  MAX_CHAINS,
  SUMMARY_STATUS,
} from './constants';

// SD-1: completed summaries and the current transient attempt are separate. This makes it
// impossible for a loading/error/cancel state to supersede the latest successfully generated
// summary merely because it is rendered after it.
export const initialState = {
  byChain: {},
};

function getChain(state, chainKey) {
  return state.byChain[chainKey] || { lastViewedAt: 0, summaries: [], attempt: null };
}

function findSummaryIndex(summaries, summaryEntryId) {
  return summaries.findIndex((summary) => summary.id === summaryEntryId);
}

function trimSummaries(summaries) {
  if (summaries.length <= MAX_SUMMARIES_PER_CHAIN) {
    return summaries;
  }
  return summaries.slice(-MAX_SUMMARIES_PER_CHAIN);
}

// Bound completed/inactive chains without discarding an in-flight request. If every cached chain
// is loading, temporarily exceed the cap; the next settled/started chain makes an inactive chain
// eligible for eviction.
function trimChains(byChain, keepChainKey) {
  const trimmed = { ...byChain };

  while (Object.keys(trimmed).length > MAX_CHAINS) {
    const leastRecentlyViewedKey = Object.keys(trimmed)
      .filter((chainKey) => chainKey !== keepChainKey && trimmed[chainKey]?.attempt?.status !== SUMMARY_STATUS.LOADING)
      .sort((a, b) => (trimmed[a]?.lastViewedAt || 0) - (trimmed[b]?.lastViewedAt || 0))[0];

    if (!leastRecentlyViewedKey) break;
    delete trimmed[leastRecentlyViewedKey];
  }

  return Object.keys(trimmed).length === Object.keys(byChain).length ? byChain : trimmed;
}

function updateAttempt(state, chainKey, entryId, updater) {
  const chain = getChain(state, chainKey);
  if (!chain.attempt || chain.attempt.id !== entryId) {
    // The attempt was replaced, completed, or never existed — late responses are no-ops.
    return state;
  }

  const updatedAttempt = updater(chain.attempt);
  if (updatedAttempt === chain.attempt) {
    return state;
  }

  return {
    ...state,
    byChain: {
      ...state.byChain,
      [chainKey]: { ...chain, attempt: updatedAttempt },
    },
  };
}

function updateSummary(state, chainKey, summaryEntryId, updater) {
  const chain = getChain(state, chainKey);
  const summaryIndex = findSummaryIndex(chain.summaries, summaryEntryId);
  if (summaryIndex === -1) {
    return state;
  }

  const currentSummary = chain.summaries[summaryIndex];
  const updatedSummary = updater(currentSummary);
  if (updatedSummary === currentSummary) {
    return state;
  }

  const summaries = [...chain.summaries];
  summaries[summaryIndex] = updatedSummary;

  return {
    ...state,
    byChain: {
      ...state.byChain,
      [chainKey]: { ...chain, summaries },
    },
  };
}

function summaryIdMatches(attempt, summaryId) {
  return attempt.summaryId == null ? summaryId == null : attempt.summaryId === summaryId;
}

// eslint-disable-next-line default-param-last
export default function summaryReducer(state = initialState, action) {
  switch (action.type) {
    case SUMMARY_ENTRY_START: {
      const { chainKey, attempt, lastViewedAt } = action.payload;
      const chain = getChain(state, chainKey);
      const byChain = trimChains(
        {
          ...state.byChain,
          [chainKey]: { ...chain, lastViewedAt, attempt },
        },
        chainKey
      );

      return {
        ...state,
        byChain,
      };
    }

    case SUMMARY_ENTRY_ACCEPTED: {
      const { chainKey, entryId, summaryId } = action.payload;
      return updateAttempt(state, chainKey, entryId, (attempt) =>
        attempt.summaryId == null || attempt.summaryId === summaryId ? { ...attempt, summaryId } : attempt
      );
    }

    case SUMMARY_ENTRY_SUCCESS: {
      const { chainKey, entryId, summaryId, summaryResponse } = action.payload;
      const chain = getChain(state, chainKey);
      const { attempt } = chain;

      // AR-1.5: a cancelled, failed, completed, or superseded attempt must never be promoted.
      if (!attempt || attempt.id !== entryId || attempt.status !== SUMMARY_STATUS.LOADING || !summaryIdMatches(attempt, summaryId)) {
        return state;
      }

      const summary = {
        id: attempt.id,
        summaryId: attempt.summaryId,
        summaryResponse,
        sourceResultParams: attempt.sourceResultParams,
        validForResultNames: [attempt.sourceResultParams.resultName],
        feedbackSubmitted: null,
      };

      const byChain = {
        ...state.byChain,
        [chainKey]: { ...chain, summaries: trimSummaries([...chain.summaries, summary]), attempt: null },
      };

      return {
        ...state,
        byChain: trimChains(byChain, chainKey),
      };
    }

    case SUMMARY_ENTRY_ERROR: {
      const { chainKey, entryId, summaryId, error } = action.payload;
      return updateAttempt(state, chainKey, entryId, (attempt) =>
        summaryIdMatches(attempt, summaryId) ? { ...attempt, status: SUMMARY_STATUS.ERROR, error, cancelled: false } : attempt
      );
    }

    case SUMMARY_ENTRY_CANCELLED: {
      const { chainKey, entryId, summaryId } = action.payload;
      return updateAttempt(state, chainKey, entryId, (attempt) =>
        summaryIdMatches(attempt, summaryId) ? { ...attempt, status: SUMMARY_STATUS.ERROR, cancelled: true, error: null } : attempt
      );
    }

    case SUMMARY_ENTRY_FEEDBACK: {
      const { chainKey, entryId, feedbackSubmitted } = action.payload;
      return updateSummary(state, chainKey, entryId, (summary) =>
        summary.feedbackSubmitted ? summary : { ...summary, feedbackSubmitted }
      );
    }

    case SUMMARY_ENTRY_ADD_VALID_RESULT_NAME: {
      const { chainKey, entryId, resultName } = action.payload;
      return updateSummary(state, chainKey, entryId, (summary) =>
        summary.validForResultNames.includes(resultName)
          ? summary
          : { ...summary, validForResultNames: [...summary.validForResultNames, resultName] }
      );
    }

    case RESET_SUMMARY_STATE: {
      return { ...initialState };
    }

    default:
      return state;
  }
}
